<template>
  <header v-if="!$route.meta?.hideChrome">
  <a href="#main-content" class="skip-link">Skip to main content</a>
  <nav class="nav nav--desktop" :class="{ scrolled }">
    <router-link to="/" class="nav-logo-link" aria-label="GYMPODS home">
      <img
        src="/img/logo-nav.webp"
        alt="GYMPODS"
        width="184"
        height="54"
        class="nav-logo"
        fetchpriority="high"
        decoding="async"
      />
    </router-link>
    <div class="nav-links">
      <div class="dropdown">
        <a class="navItem">Locations <span class="chevron" aria-hidden="true"><svg width="10" height="6" viewBox="0 0 14 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.7036 1.38221L7.07433 8.33716L0.119384 1.7079L1.29608 0.473394L7.01652 5.92596L12.4691 0.205518L13.7036 1.38221Z" fill="currentColor" /></svg></span></a>
        <div class="dropdown-content">
          <router-link to="/dalston">Dalston</router-link>
          <router-link to="/putney">Putney</router-link>
        </div>
      </div>
      <div class="dropdown dropdown--pt">
        <router-link to="/personal-training" class="navItem">Personal Training <span class="chevron" aria-hidden="true"><svg width="10" height="6" viewBox="0 0 14 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.7036 1.38221L7.07433 8.33716L0.119384 1.7079L1.29608 0.473394L7.01652 5.92596L12.4691 0.205518L13.7036 1.38221Z" fill="currentColor" /></svg></span></router-link>
        <div class="dropdown-content">
          <router-link to="/personal-training/dalston">Personal Training in Dalston</router-link>
          <router-link to="/personal-training/putney">Personal Training in Putney</router-link>
          <router-link class="nav-child--divider-top" to="/reformer-pilates-putney">Pilates in Putney</router-link>
          <router-link class="nav-child--divider-top" to="/find-a-pro">Find a Pro</router-link>
          <router-link class="nav-child--b2b" to="/gym-space-to-rent">For Trainers &amp; Therapists</router-link>
        </div>
      </div>
      <router-link class="navItem" to="/pricing">Pricing</router-link>
      <router-link class="navItem" to="/faqs">FAQs</router-link>
      <router-link class="navItem" to="/franchise">Franchise</router-link>
      <router-link v-if="authed" class="navItem navBook" data-cta="book-pod" to="/book">Book a POD</router-link>
      <router-link v-else class="navItem navJoin" data-cta="book-pod" :to="joinLink">Book a POD</router-link>
      <router-link v-if="authed" class="navItem navBook" to="/mypods">My PODs</router-link>
    </div>
  </nav>
  <div class="nav nav--mobile" role="navigation" :class="{ scrolled }">
    <router-link to="/" class="nav-logo-link" aria-label="GYMPODS home" @click="this.nav = false">
      <img
        src="/img/logo-nav.webp"
        alt="GYMPODS"
        width="184"
        height="54"
        class="nav-logo"
        fetchpriority="high"
        decoding="async"
      />
    </router-link>
    <!-- Laptop and tablet widths only (821px to 1119px): the booking pills stay
         beside Menu instead of only in the drawer. Hidden on phones. -->
    <div class="nav-pills">
      <router-link v-if="authed" class="navItem navPill" data-cta="book-pod" to="/book" @click="this.nav = false">Book a POD</router-link>
      <router-link v-else class="navItem navPill" data-cta="book-pod" :to="joinLink" @click="this.nav = false">Book a POD</router-link>
      <router-link v-if="authed" class="navItem navPill" to="/mypods" @click="this.nav = false">My PODs</router-link>
    </div>
    <button
      type="button"
      class="nav-toggle"
      :class="{ open: this.nav == true }"
      @click="toggleNav"
      :aria-expanded="this.nav ? 'true' : 'false'"
      aria-controls="mobile-nav-panel"
    >
      <span class="nav-toggle__label">{{ this.nav ? 'Close' : 'Menu' }}</span>
      <span class="nav-toggle__icon" aria-hidden="true"><i></i><i></i></span>
    </button>
    <nav id="mobile-nav-panel" :class="{ 'navOpen': this.nav == true }">
      <div class="dropdown" :class="{ 'is-open': locationDropdownVisible }">
        <a
          class="navItem"
          role="button"
          tabindex="0"
          :aria-expanded="locationDropdownVisible ? 'true' : 'false'"
          @click="locationDropdownVisible = !locationDropdownVisible"
        >Locations<ChevronDown :size="16" /></a>
        <div v-show="locationDropdownVisible" class="dropdown-content">
          <router-link to="/dalston" @click="closeNavAndDropdowns">Dalston</router-link>
          <router-link to="/putney" @click="closeNavAndDropdowns">Putney</router-link>
        </div>
      </div>
      <span class="line"></span>
      <div class="dropdown dropdown--pt" :class="{ 'is-open': ptDropdownVisible }">
        <a
          class="navItem"
          role="button"
          tabindex="0"
          :aria-expanded="ptDropdownVisible ? 'true' : 'false'"
          @click="ptDropdownVisible = !ptDropdownVisible"
        >Personal Training<ChevronDown :size="16" /></a>
        <div v-show="ptDropdownVisible" class="dropdown-content">
          <!-- On mobile the trigger above is a toggle-only button (tap
               opens the sub-menu instead of navigating), so we keep
               "Personal Training" as the first dropdown item to give
               touch users a way to reach /personal-training itself.
               Desktop dropdown omits it because the desktop trigger
               is already the router-link. -->
          <router-link to="/personal-training" @click="closeNavAndDropdowns">Personal Training</router-link>
          <router-link to="/personal-training/dalston" @click="closeNavAndDropdowns">Personal Training in Dalston</router-link>
          <router-link to="/personal-training/putney" @click="closeNavAndDropdowns">Personal Training in Putney</router-link>
          <router-link class="nav-child--divider-top" to="/reformer-pilates-putney" @click="closeNavAndDropdowns">Pilates in Putney</router-link>
          <router-link class="nav-child--divider-top" to="/find-a-pro" @click="closeNavAndDropdowns">Find a Pro</router-link>
          <router-link class="nav-child--b2b" to="/gym-space-to-rent" @click="closeNavAndDropdowns">For Trainers &amp; Therapists</router-link>
        </div>
      </div>
      <span class="line"></span>
      <router-link class="navItem" to="/pricing"
        @click="this.nav = false">Pricing</router-link>
      <span class="line"></span>
      <router-link class="navItem" to="/faqs"
        @click="this.nav = false">FAQs</router-link>
      <span class="line"></span>
      <router-link class="navItem" to="/franchise"
        @click="this.nav = false">Franchise</router-link>
      <span class="line"></span>
      <router-link v-if="authed" class="navItem navBook" data-cta="book-pod" to="/book" @click="this.nav = false"><Calendar :size="16" /> Book</router-link>
      <router-link v-else class="navItem navJoin" data-cta="book-pod" :to="joinLink" @click="this.nav = false">Book a POD</router-link>
      <router-link v-if="authed" class="navItem navBook" to="/mypods" @click="this.nav = false"><User :size="16" /> My PODs</router-link>
    </nav>
    <span v-if="this.nav === true" id="navTint" @click="toggleNav"></span>
  </div>
  </header>
  <router-view />
  <!-- Hidden on /join: the bubble overlapped PAR-Q text and crowded the Join button. -->
  <WhatsAppButton v-if="!$route.meta?.hideChrome && !onJoin" />
</template>

<script>
import { Calendar, ChevronDown, User } from 'lucide-vue-next';
import WhatsAppButton from '@/components/WhatsAppButton.vue';
import { bookPodAuth, bookPodRoute, readBookPodAuth } from '@/utils/joinRoute';

export default {
  name: 'App',
  components: { WhatsAppButton, Calendar, ChevronDown, User },
  data() {
    return {
      nav: false,
      locationDropdownVisible: false,
      ptDropdownVisible: false,
      scrolled: false,
    };
  },
  computed: {
    // Nav state is shared with the page CTAs (utils/joinRoute.js), so every
    // "Book a POD" href matches.
    authed() {
      return bookPodAuth.authed;
    },
    // Signed-out "Book a POD": log-in form for someone who has had an account on
    // this browser (gp_has_account), sign-up form otherwise.
    joinLink() {
      return bookPodRoute();
    },
    // Before the router resolves the first navigation $route has no name, so
    // fall back to the URL; otherwise the bubble flashes on /join during boot.
    onJoin() {
      return this.$route.matched.length
        ? this.$route.name === 'join'
        : window.location.pathname.replace(/\/+$/, '') === '/join';
    },
  },
  methods: {
    toggleNav() {
      this.nav = !this.nav;
    },
    // Single handler for dropdown router-links on mobile: close the drawer
    // AND collapse whichever sub-menu spawned the tap. Without this the
    // outer dropdown's visibility state drifted across sessions because
    // clicks bubbled up from child router-links to the parent toggle.
    closeNavAndDropdowns() {
      this.nav = false;
      this.locationDropdownVisible = false;
      this.ptDropdownVisible = false;
    },
    // rAF-throttled scroll handler. The previous version ran a JS function
    // on every wheel/touchmove event (high frequency on mobile — 60+ times
    // per second during a flick). Even with passive: true, that overhead
    // adds to any in-flight interaction's INP attribution. Coalescing to
    // one read + one reactive write per animation frame caps the cost.
    handleScroll() {
      if (this._scrollPending) return;
      this._scrollPending = true;
      window.requestAnimationFrame(() => {
        this._scrollPending = false;
        const next = window.scrollY > 8;
        if (next !== this.scrolled) this.scrolled = next;
      });
    },
  },
  mounted() {
    readBookPodAuth();
    // startSession (routes.js) fires this when a dead token signs the member out.
    window.addEventListener('gp-auth-change', readBookPodAuth);

    window.addEventListener('scroll', this.handleScroll, { passive: true });
    this.handleScroll();
  },
  beforeUnmount() {
    window.removeEventListener('gp-auth-change', readBookPodAuth);
    window.removeEventListener('scroll', this.handleScroll);
  },
}
</script>


<style lang="scss">
// Brand palette — kept in sync with Gympods Brandbook colours.
// Aqua / Charcoal / Carbon / Zest / Blush match the brand-book hex
// values exactly. `$midnight` is a non-brand-book working colour we
// inherit from the existing codebase (used as the dominant "deep"
// across nav-scrolled, footer, dark sections); flagged for a future
// brand decision but kept consistent at #193446 site-wide.
$aqua: #7fc0c3;
$charcoal: #231F20;
$carbon: #58595B;
$zest: #D8F789;
$blush: #F9A874;
$midnight: #193446;

/*
 * Manrope Regular & Bold @font-face rules live in public/css/fonts.css
 * (linked from public/index.html) so they keep the same stable /fonts/
 * URL as the <link rel="preload"> tags without going through webpack's
 * css-loader, and they survive critters' inline-style stripping.
 * Medium/SemiBold/Light remain below — those are webpack-bundled because
 * they're not critical to first paint and don't need preloading.
 */

@font-face {
  font-family: 'Manrope';
  src: url('./assets/fonts/Manrope-Medium.woff2') format('woff2'),
       url('./assets/fonts/Manrope-Medium.ttf') format('truetype');
  font-weight: 500;
  font-display: optional;
}

@font-face {
  font-family: 'Manrope';
  src: url('./assets/fonts/Manrope-SemiBold.woff2') format('woff2'),
       url('./assets/fonts/Manrope-SemiBold.ttf') format('truetype');
  font-weight: 600;
  font-display: optional;
}

@font-face {
  font-family: 'Manrope';
  src: url('./assets/fonts/Manrope-Light.woff2') format('woff2'),
       url('./assets/fonts/Manrope-Light.ttf') format('truetype');
  font-weight: lighter;
  font-display: optional;
}

body {
  padding: 0;
  margin: 0;
  overflow-x: hidden;
}

// Desktop nav from 1120px, Menu layout up to $nav-bp (1119px). One breakpoint for
// signed in and signed out, so the header doesn't change layout at log-in. It
// is the narrowest width where the signed-in nav (Book a POD + My PODs) fits
// with its spacing at the minimum below. Both navs are in the markup and CSS
// picks one: the prerender runs at 375px, so a width check in JS shipped the
// mobile header to desktop until the app mounted.
$nav-bp-n: 1119;
$nav-bp: $nav-bp-n * 1px;

// .nav.nav--* so these beat .nav's own display: flex further down.
.nav.nav--mobile { display: none; }

@media screen and (max-width: $nav-bp) {
  .nav.nav--desktop { display: none; }
  .nav.nav--mobile { display: flex; }
}

// Resting height of the fixed top nav — the single source for the nav
// itself and every page offset that clears it (TasterPack spacer,
// JoinView, MyPods, Password, the mobile drawer).
:root {
  --nav-h: 88px;
}

@media screen and (max-width: $nav-bp) {
  :root {
    --nav-h: 68px;
  }
}

// Form controls don't inherit font-family by default (buttons were
// rendering in Arial). family only — `font: inherit` would also reset
// size/line-height on every unsized control site-wide.
button,
input,
select,
textarea {
  font-family: inherit;
}

#app {
  font-family: 'Manrope', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: $charcoal;
  padding: 0;
  margin: 0;
  overflow-x: hidden;
}

.skip-link {
  position: absolute;
  top: -100%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  background: #231F20;
  color: #fff;
  padding: 12px 24px;
  font-size: 1rem;
  font-family: 'Manrope', sans-serif;
  font-weight: 600;
  text-decoration: none;
  border-radius: 0 0 8px 8px;
  white-space: nowrap;
  &:focus {
    top: 0;
    outline: 2px solid #D8F789;
    outline-offset: 2px;
  }
}

.nav {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: var(--nav-h);
  padding: 0 max(24px, 5%);
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(180deg, rgba($aqua, 0.92) 0%, rgba(darken($aqua, 3%), 0.88) 100%);
  // Backdrop-filter is expensive — each scroll frame re-samples the
  // pixels behind the nav. Dropping saturate() and halving blur radius
  // cuts the filter cost roughly in half while preserving the frosted
  // feel. will-change: transform keeps the nav in its own compositor
  // layer so state transitions (scrolled / not) don't force full
  // repaints of the whole page.
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-bottom: 1px solid rgba($midnight, 0.08);
  z-index: 100;
  will-change: transform;
  transform: translateZ(0);
  transition: background 0.35s ease, border-color 0.35s ease, box-shadow 0.35s ease, height 0.3s ease;

  &.scrolled {
    background: linear-gradient(180deg, $aqua 0%, darken($aqua, 5%) 100%);
    border-bottom-color: rgba($midnight, 0.18);
    box-shadow: 0 10px 30px -14px rgba($midnight, 0.4);
  }

  // Desktop shrinks on scroll; the mobile nav keeps its resting height.
  @media screen and (min-width: $nav-bp + 1) {
    &.scrolled {
      height: 72px;
    }
  }
}

// Below 1440px the desktop nav's spacing shrinks with the window, so it fits
// down to 1120px without shrinking text or buttons. From 1440px up it is
// unchanged: 5% side padding (72px at 1440) and 20px --nav-x. --nav-x is the
// padding either side of each link (and its underline inset) and the space
// before each pill; it runs 10px at $nav-bp to 20px at 1439px. The side
// padding runs 20px (as on the mobile header) to 72px over the same range,
// then follows 5%. (The ramps end at 1439, not 1440, so Sass's rounding of
// the slope can't leave 1440 a fraction of a pixel short.) gap keeps the
// logo at least 24px clear of the links.
.nav--desktop {
  --nav-x: clamp(10px, calc(10px + (100vw - #{$nav-bp}) * 10 / #{1439 - $nav-bp-n}), 20px);
  padding: 0 max(20px, min(5%, calc(20px + (100vw - #{$nav-bp}) * 52 / #{1439 - $nav-bp-n})));
  gap: 24px;
}

.nav-logo-link {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  line-height: 0;
  text-decoration: none;
}

.nav-logo {
  height: 54px;
  width: auto;
  cursor: pointer;
  transition: opacity 0.25s ease, height 0.3s ease;
  flex-shrink: 0;

  &:hover { opacity: 0.85; }
}

// Desktop logo shrinks with the scrolled nav; mobile keeps its size.
@media screen and (min-width: $nav-bp + 1) {
  .nav.scrolled .nav-logo {
    height: 44px;
  }
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 2px;
}

.dropdown {
  position: relative;
  display: inline-block;

  .chevron {
    display: inline-flex;
    align-items: center;
    margin-left: 6px;
    color: rgba(255, 255, 255, 0.75);
    transition: transform 0.3s ease, color 0.25s ease;
  }

  &:hover .chevron {
    transform: rotate(180deg);
    color: #fff;
  }

  /* Invisible bridge so hover doesn't drop when moving into the floating panel.
     The panel is min-width 240px and centered on the trigger. The old bridge
     (`left: -20px; right: -20px`) was only ~40px wider than the trigger itself —
     at narrow desktop widths, the cursor could move diagonally from the
     trigger toward a menu item and cross through a dead zone outside the
     bridge but still above the panel's top edge. Hover would drop and the
     panel would vanish before the cursor reached it.

     Solution: make the bridge a fixed 320px wide, centered on the trigger,
     so it comfortably covers the full panel width (240px) + 40px safety on
     each side. */
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    width: 320px;
    height: 18px;
    transform: translateX(-50%);
    pointer-events: none;
  }

  &:hover::after {
    pointer-events: auto;
  }
}

.dropdown-content {
  position: absolute;
  // Panel starts exactly at the trigger's bottom edge so there is NO
  // unhoverable dead zone between trigger and panel. The 14px visible
  // gap is created by the top padding, which IS part of the panel's
  // hit region — the hover chain is unbroken from trigger to menu item.
  top: 100%;
  left: 50%;
  min-width: 240px;
  padding: 14px 0 0;
  background: transparent;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transform: translateX(-50%) translateY(-4px);
  transition: opacity 0.22s ease, transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), visibility 0.22s;
  z-index: 10;

  // The visible white card is drawn by a pseudo-element that sits below
  // the transparent 14px hover band. This keeps the card's rounded
  // corners, border and shadow all aligned with what the user sees,
  // while the outer `.dropdown-content` remains a transparent hoverable
  // hit area that reaches all the way up to the trigger.
  &::before {
    content: '';
    position: absolute;
    top: 14px;
    left: 0;
    right: 0;
    bottom: 0;
    background: #ffffff;
    border: 1px solid rgba($midnight, 0.06);
    border-radius: 16px;
    box-shadow:
      0 24px 48px -16px rgba($midnight, 0.28),
      0 6px 16px -6px rgba($midnight, 0.08);
    z-index: -1;
  }

  // Inner wrapper padding so the links have breathing room inside the
  // visible white card, without needing a second DOM element.
  > * {
    margin-left: 10px;
    margin-right: 10px;
  }

  > *:first-child {
    margin-top: 10px;
  }

  > *:last-child {
    margin-bottom: 10px;
  }

  a {
    position: relative;
    display: flex;
    align-items: center;
    padding: 12px 18px;
    margin: 2px 10px;
    font-size: 1rem;
    font-weight: 500;
    letter-spacing: 0.01em;
    color: $charcoal;
    text-align: left;
    border-radius: 10px;
    // Only animate colour/background — NOT padding-left. The old shift
    // (18px → 28px) made items physically move away from the cursor,
    // which read as "the options are dancing around when I hover near
    // them". Keeping text stationary means the item stays where the
    // user aimed.
    transition: background 0.22s ease, color 0.22s ease;

    &:hover,
    &.router-link-exact-active {
      background: rgba($aqua, 0.12);
      color: darken($aqua, 28%);
    }
  }
}

.dropdown:hover .dropdown-content {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  transform: translateX(-50%) translateY(0);
}

// ── Desktop: PT dropdown is wider than the shared 240px min so
// "Personal Training in Dalston" / "Personal Training in Putney" fit
// on one line. Scoped to `.dropdown--pt` so the Locations dropdown
// (short items "Dalston", "Putney") keeps its narrower default.
.dropdown--pt .dropdown-content {
  min-width: 280px;

  a { white-space: nowrap; }
}

// ── Desktop: lane separators in the Personal Training dropdown ──
// Scoped to `.dropdown--pt` so the shared `.dropdown-content` rule (and the
// Locations dropdown) are untouched. Hairline midnight divider above the
// row so it reads as a distinct lane; hover/active state inherits the
// shared aqua tint below, on purpose — no button-like fill.
// Own `position: relative` so the ::before anchor doesn't rely on the
// shared `.dropdown-content a` positioning (safer if that ever changes).
// Applied to two rows: `.nav-child--divider-top` (Find a Pro, above the
// consumer/pro lane split) and `.nav-child--b2b` (For Trainers, above
// the B2B lane).
.dropdown--pt .dropdown-content .nav-child--divider-top,
.dropdown--pt .dropdown-content .nav-child--b2b {
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -3px;
    left: 12px;
    right: 12px;
    height: 1px;
    background: rgba($midnight, 0.09);
  }
}

.LandingHeaderDiv {
  height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.LandingHeader {
  position: relative;
  color: white;
  font-size: clamp(20px, 10vw, 100px);
  text-transform: uppercase;
  line-height: clamp(20px, 10vw, 100px);
  letter-spacing: 0.2em;
  text-align: left;
  max-width: 1440px;
  display: block;
  position: relative;
  margin: auto;
  padding: 50px;
  padding-top: 100px;
  z-index: 3;
}

.LandingHeaderButton {
  padding: 10px 60px;
  color: white;
  background: none;
  border: solid 3px white;
  font-size: 20px;
  cursor: pointer;
}

.LandingHeaderButton:hover {
  background: white;
  color: $charcoal;
}

h2 {
  color: $charcoal;
  font-style: normal;
  font-size: 30px;
  line-height: 49px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  margin: 0 0 25px 0;
}

h3,
h4 {
  color: $charcoal;
  font-style: normal;
  font-size: 1.25em;
  line-height: 30px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  margin: 20px 0;
}

h4 {
  font-size: 0.8em;
}

p {
  margin: 0.4em 0;
}

a,
a:visited {
  color: inherit;
  text-decoration: inherit;
}

.background-aqua-to-zest {
  background-image: linear-gradient(90deg, $aqua, $zest);
}

.background-zest-to-aqua {
  background-image: linear-gradient(270deg, $aqua, $zest);
}

.width {
  max-width: 1440px;
  margin: 0 auto;
}

.singleColumn {
  max-width: 1440px;
  margin: 0 auto;
  width: 100vw;
  box-sizing: border-box;
  position: relative;
}

.singleColumn.fullWidth {
  max-width: unset;
}

.singleColumn.square {
  max-height: 720px;
  height: 100vw;
  display: grid;
  align-items: center;
  justify-items: center;
}

.singleColumn.narrow {
  max-width: 720px;
}

.singleColumn.padded {
  padding: 25px;
}

.twoColumns {
  display: grid;
  grid-template-columns: 50% 50%;
  max-width: 1440px;
  width: 100vw;
  margin: 0 auto 0 auto;
  box-sizing: border-box;
}

.twoColumns.twoThirds {
  grid-template-columns: 67% 33%;
}

.twoColumns.fullWidth {
  width: 100vw;
  max-width: 100vw;
}

.carousel .twoColumns {
  display: grid;
}

.carousel .twoColumns {
  filter: unset;
}

.carousel.twoColumnsSquare {
  height: 50vw;
  width: 100vw;
}


.twoColumns>div {
  display: grid;
  align-items: center;
  justify-items: center;
  padding: 10px;
  box-sizing: border-box;
}

.twoColumns.square>div {
  width: 50vw;
  max-width: 720px;
  max-height: 720px;
  height: 50vw;
}

.whiteBtn {
  background: none;
  border: solid 3px white;
  color: white;
  padding: 10px 60px;
  font-size: 20px;
  cursor: pointer;
}

.whiteBtn:hover {
  background: white;
  color: $charcoal;
}

.blackBtn {
  @extend .whiteBtn;
  border: solid 3px $charcoal;
  color: $charcoal;
}

.blackBtn:hover {
  background: $charcoal;
  color: white;
}

.nav .navItem {
  color: #fff;
}

.navItem {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 12px var(--nav-x, 20px);
  margin: 0 2px;
  font-size: 1.1rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: #fff;
  cursor: pointer;
  transition: opacity 0.25s ease;
  // Lock to a single line so the trigger's height is predictable at every
  // viewport width. Without this, a squeezed navItem could wrap text
  // (the chevron + "Locations" becoming two lines), doubling the trigger
  // height and pushing the dropdown's `top: 100%` offset proportionally
  // further down the page — which read as "panel appearing in the wrong
  // place" at narrow desktop widths.
  white-space: nowrap;

  &::after {
    content: '';
    position: absolute;
    left: var(--nav-x, 20px);
    right: var(--nav-x, 20px);
    bottom: 4px;
    height: 1.5px;
    background: #fff;
    border-radius: 2px;
    transform: scaleX(0);
    transform-origin: center;
    transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
  }

  &:hover,
  &.router-link-exact-active {
    color: #fff;
    text-shadow: none;

    &::after { transform: scaleX(1); }
  }
}

.dropdown:hover > .navItem {
  color: #fff;
}

.nav .navBook,
.nav .navJoin,
.nav .navPill {
  margin-left: var(--nav-x, 20px);
  padding: 10px 22px;
  color: $charcoal;
  background: $zest;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  text-transform: none;
  white-space: nowrap;
  flex-shrink: 0;
  border-radius: 999px;
  box-shadow: none;
  transition: transform 0.25s ease, background 0.25s ease, color 0.25s ease;

  &::after { display: none; }

  &:hover {
    color: $charcoal;
    background: lighten($zest, 3%);
    transform: translateY(-1px);
  }

  &.router-link-exact-active {
    color: $charcoal;
  }
}

@-webkit-keyframes spin {
  0% {
    -webkit-transform: rotate(0deg);
  }

  100% {
    -webkit-transform: rotate(360deg);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.loader {
  display: inline-block;
  border: 2px solid #f5f5f5;
  border-radius: 50%;
  border-top: 2px solid #030303;
  width: 15px;
  height: 15px;
  -webkit-animation: spin 1s linear infinite;
  animation: spin 1s linear infinite;
  margin: auto;
}

@media screen and (max-width: $nav-bp) {
  .nav {
    padding: 0 20px;

    // Pushed right, next to Menu, with the logo staying left.
    .nav-pills {
      display: flex;
      align-items: center;
      gap: 10px;
      margin: 0 12px 0 auto;

      .navPill { margin-left: 0; }
    }

    .nav-logo {
      width: 140px;
      height: auto;
    }

    .nav-toggle {
      position: relative;
      display: inline-flex;
      align-items: center;
      gap: 10px;
      height: 44px;
      padding: 0 14px 0 18px;
      border: 0;
      border-radius: 999px;
      background: $midnight;
      color: #fff;
      font-family: 'Manrope', sans-serif;
      font-size: 15px;
      font-weight: 700;
      line-height: 1;
      letter-spacing: 0.2px;
      cursor: pointer;
      z-index: 4;
      transition: background 0.2s ease, color 0.2s ease;

      &:focus-visible {
        outline: 2px solid $midnight;
        outline-offset: 2px;
      }

      .nav-toggle__icon {
        position: relative;
        width: 20px;
        height: 12px;

        i {
          position: absolute;
          right: 0;
          height: 2.2px;
          border-radius: 2px;
          background: $zest;
          transition: transform 0.2s ease, width 0.2s ease, top 0.2s ease, background 0.2s ease;
        }

        i:first-child {
          top: 0;
          width: 20px;
        }

        i:last-child {
          top: calc(100% - 2.2px);
          width: 11px;
        }
      }

      &.open {
        background: $zest;
        color: $midnight;

        .nav-toggle__icon i {
          top: calc(50% - 1.1px);
          width: 20px;
          background: $midnight;
        }

        .nav-toggle__icon i:first-child {
          transform: rotate(45deg);
        }

        .nav-toggle__icon i:last-child {
          transform: rotate(-45deg);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        &,
        .nav-toggle__icon i {
          transition: none;
        }
      }
    }

    nav {
      width: 0px;
      // Use small viewport height (svh) so the drawer caps at the *visible*
      // height on mobile browsers — vh ignores the dynamic URL bar and can
      // overshoot. Fall back to vh for browsers without svh.
      height: calc(100vh - var(--nav-h));
      height: calc(100svh - var(--nav-h));
      // Padding (8 + 40) was inflating the box because the default
      // box-sizing on <nav> is content-box: a 382px height calc + 48px
      // padding = 430px rendered, pushing the drawer 48px below short
      // viewports and clipping the bottom items even with overflow-y:auto.
      box-sizing: border-box;
      overflow-x: hidden;
      overflow-y: auto;
      // Allow momentum scrolling on iOS so users can reach items that
      // overflow when a sub-menu is expanded on a short viewport.
      -webkit-overflow-scrolling: touch;
      // Stop scroll chaining to the page underneath when the drawer
      // reaches its scroll limit.
      overscroll-behavior: contain;
      position: fixed;
      top: var(--nav-h);
      right: 0;
      left: auto;
      transition: width 0.32s cubic-bezier(0.22, 1, 0.36, 1);
      background: linear-gradient(180deg, rgba(darken($midnight, 3%), 0.98) 0%, rgba(darken($midnight, 8%), 0.98) 100%);
      backdrop-filter: blur(18px) saturate(150%);
      -webkit-backdrop-filter: blur(18px) saturate(150%);
      border-left: 1px solid rgba($aqua, 0.12);
      text-align: left;
      display: flex;
      flex-direction: column;
      white-space: nowrap;
      padding: 8px 0 40px;

      .line {
        width: calc(100% - 48px);
        height: 1px;
        background: linear-gradient(90deg, rgba($aqua, 0.18), rgba($aqua, 0.04));
        margin: 0 24px;
      }

      .dropdown-content {
        position: relative;
        display: block;
        background: transparent;
        backdrop-filter: none;
        -webkit-backdrop-filter: none;
        border: none;
        border-radius: 0;
        box-shadow: none;
        opacity: 1;
        visibility: visible;
        transform: none;
        // Reset desktop's positional offsets. Desktop has `top: 100%` and
        // `left: 50%` so its absolutely-positioned panel floats below and
        // centred on the trigger. On mobile the sub-menu is in normal flow
        // (position: relative), so those offsets instead shift the
        // sub-menu 100% of the parent's height down and 50% to the right —
        // pushing Dalston/Putney off-screen horizontally AND overlapping
        // the items below vertically.
        top: auto;
        left: auto;
        padding: 4px 0 10px 40px;
        min-width: 0;

        // Kill the desktop white-card pseudo. On mobile the drawer has its
        // own dark background; the leftover ::before painted a stray white
        // panel inside the drawer (and bled past the overflow-x clip).
        &::before { display: none; }

        a {
          display: block;
          padding: 10px 20px;
          margin: 0;
          font-size: 1.05rem;
          font-weight: 500;
          letter-spacing: 0.04em;
          color: rgba(255, 255, 255, 0.75);
          text-transform: none;
          border-radius: 8px;

          &::before { display: none; }

          &:hover,
          &.router-link-exact-active {
            color: $aqua;
            background: transparent;
            padding-left: 20px;
          }
        }
      }

      // ── Mobile: lane separators in the Personal Training accordion ──
      // Scoped to `.dropdown--pt` so the shared `.dropdown-content` mobile
      // override (and the Locations accordion) are untouched. Hairline aqua
      // divider above only — matches the drawer's `.line` accent vocabulary.
      // Hover state inherits the shared aqua-on-midnight rule below.
      // Applied to two rows: `.nav-child--divider-top` (Find a Pro) and
      // `.nav-child--b2b` (For Trainers).
      .dropdown--pt .dropdown-content .nav-child--divider-top,
      .dropdown--pt .dropdown-content .nav-child--b2b {
        position: relative;
        padding-top: 14px;
        margin-top: 4px;

        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 20px;
          right: 20px;
          height: 1px;
          background: rgba($aqua, 0.18);
        }
      }

      .navItem {
        position: relative;
        font-weight: 500;
        letter-spacing: 0.12em;
        padding: 22px 24px 8px;
        margin: 0;
        font-size: 1.35rem;
        text-transform: uppercase;
        color: rgba(255, 255, 255, 0.92);
        width: 100%;
        box-sizing: border-box;

        &::after { display: none; }

        &:hover,
        &.router-link-exact-active {
          color: $aqua;
        }

        i {
          font-size: 0.55em;
          display: inline-block;
          margin-left: 10px;
          vertical-align: middle;
          color: rgba($aqua, 0.8);
          transition: transform 0.25s ease;
        }

        svg {
          margin-left: 6px;
        }
      }

      // Rotate the chevron when the mobile drawer sub-menu is open, so the
      // state is visible at a glance (previously no open-state indicator).
      .dropdown.is-open > .navItem > i {
        transform: rotate(180deg);
        color: $aqua;
      }

      // Neutralise the desktop `.dropdown:hover .dropdown-content` rule on
      // mobile. That rule reapplies `transform: translateX(-50%)`, which
      // is fine when the panel is absolutely positioned and centred under
      // a desktop trigger but on mobile shifts the in-flow sub-menu ~170px
      // to the left of the drawer — Dalston/Putney slid off the visible
      // edge the moment the cursor entered them and the menu appeared to
      // "disappear" on hover.
      .dropdown:hover .dropdown-content {
        transform: none;
      }
    }

    .navOpen {
      width: min(86vw, 380px);
      z-index: 3;
      box-shadow: -24px 0 60px -12px rgba(0, 0, 0, 0.55);
    }

    .navBook,
    .navJoin {
      color: $charcoal !important;
      background: $zest !important;
      margin: 28px 24px 0 !important;
      padding: 14px 22px !important;
      width: fit-content !important;
      text-transform: none !important;
      font-size: 0.95rem !important;
      font-weight: 700 !important;
      letter-spacing: 0.01em !important;
      border-radius: 999px !important;
      // Match desktop: no glow. The zest-tinted drop-shadow made the
      // button look like it was emitting light against the dark drawer.
      box-shadow: none;

      i { color: $charcoal !important; }
    }

    #navTint {
      width: 100vw;
      height: calc(100vh - var(--nav-h));
      position: fixed;
      top: var(--nav-h);
      left: 0;
      z-index: 2;
      background-color: rgba(0, 0, 0, 0.55);
      backdrop-filter: blur(2px);
      -webkit-backdrop-filter: blur(2px);
    }
  }
}

@media screen and (max-width: 820px) {
  .nav .nav-pills { display: none; }

  .LandingHeader,
  .LandingHeaderDiv {
    box-sizing: border-box;
    height: 500px;
    // margin-top: 160px;
    // padding: 0;
  }

  .LandingHeaderButton {
    margin-top: 50px;
  }

  .twoColumns {
    grid-template-columns: unset;

    div {
      width: 100vw;
      height: 100vw;
    }
  }
}
</style>
